import { FC, memo, useCallback } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useGetTasks } from '../hooks';
import { useTaskCenterStore } from '../provider';
import { taskCenterTrackingIds } from '../tracking-ids';
import { getTaskEmptyState } from '../utils';
import { TaskItem } from './common';

interface TaskByStatusProps {
  status: TaskCenterTypes.TaskStatus;
}

export const TaskByStatus: FC<TaskByStatusProps> = memo(({ status }) => {
  const { t } = useTranslation('task-center');
  const { hasNextPage, isFetchingNextPage, fetchNextPage, status: queryStatus, data, isFetching } = useGetTasks(status);
  const { setTaskMode } = useTaskCenterStore(['setTaskMode']);

  const openCreateTask = useCallback(() => setTaskMode('create'), [setTaskMode]);

  const list = data ? data.pages.flatMap((page) => page.tasks).filter((task) => !!task) : [];

  const emptyStateConfig = {
    type: getTaskEmptyState(status),
    ...(status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && {
      action: {
        label: t('Create Task'),
        onClick: openCreateTask,
        trackingId: taskCenterTrackingIds.createTaskButton,
        'aria-label': t('Create Task'),
      },
    }),
    header: t('No Tasks Found'),
    description: t('Try adjusting filters, check other tabs, or create a new task to get started.'),
  };

  return (
    <PaginatedList
      listData={list}
      renderListItem={({ listItem }) => <TaskItem key={listItem.id} {...listItem} />}
      hasNextPage={hasNextPage}
      isError={queryStatus === 'error'}
      isLoading={queryStatus === 'loading' || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={20}
      loadMore={fetchNextPage}
      emptyStateConfig={emptyStateConfig}
      endOfListText=''
    />
  );
});

TaskByStatus.displayName = 'TaskByStatus';
